import { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import useCurrencies from "../../hooks/useCurrencies";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import Select from "react-select";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CoordinatePicker from "../../components/CustomComponents/Maps/CoordinatePicker";
import { Icon } from "@mui/material";
import moment from "moment";
import { updateUserData } from "../../app/auth/authSlice";
import { appSettings } from "../../app/appSettingsSlice";

const mapApiToState = (apiData, settingsData) => ({
  CompID: apiData.compID || 0,
  CompName: apiData.compName || "",
  CompanySlug:
    apiData.companySlug && apiData.companySlug !== "NULL"
      ? apiData.companySlug
      : apiData.CompanySlug && apiData.CompanySlug !== "NULL"
      ? apiData.CompanySlug
      : "",
  CompDetail: apiData.compDetail || "",
  CompPhone: apiData.compPhone || "",
  CompEmail: apiData.compEmail || "",
  CompCountry: apiData.compCountry || "UK",
  CompCity: apiData.compCity || "",
  CompLanguage: apiData.compLanguage || "",
  CompCurrency: apiData.compCurrency || "",
  CompTimeZone: apiData.compTimeZone || "",
  CompUTCTimeZone: apiData.compUTCTimeZone || "",
  CompAddress: apiData.compAddress || "",
  CompWebsite: apiData.compWebsite || "",
  CompFax: apiData.compFax || "",
  CompIndustryType: apiData.compIndustryType || "",
  AndroidAppver: apiData.androidAppver || "",
  IOSAppver: apiData.iosAppver || "",
  CompSecCode: apiData.compSecCode || "",
  CompLatitude: apiData.compLatitude || 0,
  CompLongitude: apiData.compLongitude || 0,
  CompIcon: apiData.compIcon || "",
  CompCoverImage: apiData.compCoverImage || apiData.CompCoverImage || "",
  MinOrderAmount: settingsData?.s_MinOrderAmount || 0,
  UpdateProCPriceAgainstMasterProductCPriceUpdate:
    apiData.updateProCPriceAgainstMasterProductCPriceUpdate || false,
  CompPNotificationLimit_PerDay: apiData.compPNotificationLimit_PerDay || 0,
  CompMaxIPadLicense: apiData.compMaxIPadLicense || 0,
  CompMaxUserLicense: apiData.compMaxUserLicense || 0,
  WaiterAppMaxDevices: apiData.waiterAppMaxDevices || 0,
  CompRegDate: apiData.compRegDate || "",
  CompExpireDate: apiData.compExpireDate || "",
  LocationLicences: apiData.locationLicences || 0,
  MobileAppExpireDate: apiData.mobileAppExpireDate || "",
  WebAppExpireDate: apiData.webAppExpireDate || "",
  WaiterAppExpireDate: apiData.waiterAppExpireDate || "",
  IsKitchenApp: apiData.isKitchenApp || false,
  ISMobileApp: apiData.isMobileApp || false,
  IsTVApp: apiData.isTVApp || false,
  ISWebApp: apiData.isWebApp || false,
  IsWaiterApp: apiData.isWaiterApp || false,
  CompPublish: apiData.compPublish || false,
  CompRecoveryEmail: apiData.compRecoveryEmail || "",
  CompRecoveryPhoneNumber: apiData.compRecoveryPhoneNumber || "",
  CompFacebooklink: apiData.compFacebooklink || "",
  CompInstagramLink: apiData.compInstagramLink || "",
  CompYoutubeLink: apiData.compYoutubeLink || "",
  CompTikTokLink: apiData.compTikTokLink || "",
  CompGoogleReview: apiData.compGoogleReview || "",
});

function Profile() {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData, settingsData } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());
  const [isGeneralEditing, setIsGeneralEditing] = useState(false);
  const [generalPreviousData, setGeneralPreviousData] = useState(null);
  const [isLogoEditing, setIsLogoEditing] = useState(false);
  const [logoPreviousData, setLogoPreviousData] = useState(null);
  const [isSocialLinksEditing, setIsSocialLinksEditing] = useState(false);
  const [socialLinksPreviousData, setSocialLinksPreviousData] = useState(null);
  const [isBusinessDetailsEditing, setIsBusinessDetailsEditing] =
    useState(false);
  const [businessDetailsPreviousData, setBusinessDetailsPreviousData] =
    useState(null);
  const [isSecurityEditing, setIsSecurityEditing] = useState(false);
  const [securityPreviousData, setSecurityPreviousData] = useState(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings || {};

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  const [companyData, setCompanyData] = useState({
    CompID: CompID || 0,
    CompName: "",
    CompanySlug: "",
    CompDetail: "",
    CompPhone: "",
    CompEmail: "",
    CompCountry: "UK",
    CompCity: "",
    CompLanguage: "",
    CompCurrency: "",
    CompTimeZone: "",
    CompUTCTimeZone: "",
    CompAddress: "",
    CompWebsite: "",
    CompFax: "",
    CompIndustryType: "",
    AndroidAppver: "",
    IOSAppver: "",
    CompSecCode: "",
    CompLatitude: 0,
    CompLongitude: 0,
    CompIcon: "",
    CompCoverImage: "",
    MinOrderAmount: 0,
    UpdateProCPriceAgainstMasterProductCPriceUpdate: false,
    CompPNotificationLimit_PerDay: 0,
    CompMaxIPadLicense: 0,
    CompMaxUserLicense: 0,
    WaiterAppMaxDevices: 0,
    CompRegDate: "",
    CompExpireDate: "",
    LocationLicences: 0,
    MobileAppExpireDate: "",
    WebAppExpireDate: "",
    WaiterAppExpireDate: "",
    IsKitchenApp: false,
    ISMobileApp: false,
    IsTVApp: false,
    ISWebApp: false,
    IsWaiterApp: false,
    CompPublish: false,
    CompRecoveryEmail: "",
    CompRecoveryPhoneNumber: "",
    CompFacebooklink: "",
    CompInstagramLink: "",
    CompYoutubeLink: "",
    CompTikTokLink: "",
    CompGoogleReview: "",
  });
  const { currencies } = useCurrencies();
  const [timeZones, setTimeZones] = useState([]);
  const [languages, setLanguages] = useState([
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" },
    { value: "it", label: "Italian" },
  ]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [logoState, setLogoState] = useState({
    CompIcon: "",
    logoFile: null,
  });
  const [coverState, setCoverState] = useState({
    CompCoverImage: "",
    coverFile: null,
  });

  const fetchTimeZones = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_TIMEZONE);
      setTimeZones(response.data.data);
    } catch (err) {
      showErrorToast("Failed to load time zones");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanyData = useCallback(
    async (setLoadingState = true) => {
      try {
        if (setLoadingState) {
          setLoading(true);
        }
        // Fetch fresh data from APIs
        const [profileResponse, settingsResponse] = await Promise.all([
          axiosInstance.get(APIs.GET_COMPANY_PROFILE),
          axiosInstance.get(APIs.GET_COMPANY_SETTINGS),
        ]);

        const freshProfileData = profileResponse.data.data;
        const freshSettingsData = settingsResponse.data.data;

        // Update Redux state with fresh data
        dispatch(
          updateUserData({
            profileData: freshProfileData,
            settingsData: freshSettingsData,
            lastDataRefresh: new Date().toISOString(),
          })
        );

        const mappedData = mapApiToState(freshProfileData, freshSettingsData);
        setCompanyData(mappedData);
        setLogoState({
          CompIcon: mappedData.CompIcon || "",
          logoFile: null,
        });
        setCoverState({
          CompCoverImage: mappedData.CompCoverImage || "",
          coverFile: null,
        });
        setImageKey(Date.now());
      } catch (err) {
        showErrorToast("Failed to load Company Data");
      } finally {
        if (setLoadingState) {
          setLoading(false);
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchTimeZones();
    fetchCompanyData();
  }, []);

  // Complete fixed useEffect for scroll detection and sidebar highlighting:

  // Replace the existing useEffect for scroll detection with this improved version:

  useEffect(() => {
    const initializeScrollSpy = () => {
      const sections = document.querySelectorAll("#bsSpyContent > section");
      const navLinks = document.querySelectorAll("#Target > a");

      if (sections.length === 0 || navLinks.length === 0) {
        setTimeout(initializeScrollSpy, 100);
        return;
      }

      console.log(
        "ScrollSpy initialized with sections:",
        Array.from(sections).map((s) => s.id)
      );

      // Add smooth scrolling to navigation links
      const clickHandlers = [];
      navLinks.forEach((link) => {
        const clickHandler = (e) => {
          e.preventDefault();
          const targetId = link.getAttribute("href").substring(1);
          const targetSection = document.getElementById(targetId);
          if (targetSection) {
            // Remove active state from all links before scrolling
            setActiveNavLink(null);

            targetSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });

            // Set active state after scroll completes
            setTimeout(() => setActiveNavLink(targetId), 500);
          }
        };
        link.addEventListener("click", clickHandler);
        clickHandlers.push({ link, handler: clickHandler });
      });

      function setActiveNavLink(activeId) {
        navLinks.forEach((link) => {
          const linkId = link.getAttribute("href").substring(1);

          // Reset all links to default styles
          link.classList.remove("active", "text-white");
          link.style.borderLeft = "3px solid transparent";
          link.style.backgroundColor = "transparent";
          link.style.borderRadius = "6px";

          // Set default colors based on theme
          if (isDarkMode) {
            link.style.color = "#d1d5db";
            link.classList.add("hover:text-white", "hover:bg-gray-700");
            link.classList.remove(
              "text-gray-700",
              "hover:text-[#232B2B]",
              "hover:bg-gray-100"
            );
          } else {
            link.style.color = "#374151";
            link.classList.add(
              "text-gray-700",
              "hover:text-[#232B2B]",
              "hover:bg-gray-100"
            );
            link.classList.remove("hover:text-white", "hover:bg-gray-700");
          }

          // Apply active styles to the active link
          if (linkId === activeId) {
            link.classList.add("active", "text-white");
            link.classList.remove(
              "text-gray-700",
              "hover:text-[#232B2B]",
              "hover:bg-gray-100",
              "hover:text-white",
              "hover:bg-gray-700"
            );
            link.style.borderLeft = `3px solid ${themeColor}`;
            link.style.backgroundColor = themeColor;
            link.style.color = "#ffffff";
            link.style.borderRadius = "6px";
          }
        });
      }

      function handleScroll() {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;

        let activeSection = null;
        let bestScore = -1;

        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          const sectionTop = scrollTop + rect.top;
          const sectionBottom = sectionTop + rect.height;
          const sectionHeight = rect.height;

          // Calculate visibility score
          let score = 0;

          // Check if section is in viewport
          const viewportTop = scrollTop;
          const viewportBottom = scrollTop + windowHeight;

          // Calculate visible portion
          const visibleTop = Math.max(viewportTop, sectionTop);
          const visibleBottom = Math.min(viewportBottom, sectionBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > 0) {
            // Score based on how much is visible and position
            const visibilityRatio = visibleHeight / sectionHeight;
            const positionScore = rect.top <= 100 ? 2 : 1; // Prefer sections near top

            score = visibilityRatio * positionScore;

            // Boost score for sections that start within viewport top
            if (rect.top >= 0 && rect.top <= 200) {
              score += 1;
            }

            // Special handling for first section when at top of page
            if (section.id === "companyLogo" && scrollTop < 50) {
              score += 2;
            }
          }

          if (score > bestScore) {
            bestScore = score;
            activeSection = section.id;
          }
        });

        // Fallback: if no section scored well, find the closest one to center of viewport
        if (!activeSection || bestScore < 0.1) {
          let minDistance = Infinity;
          const viewportCenter = scrollTop + windowHeight / 2;

          sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            const sectionCenter = scrollTop + rect.top + rect.height / 2;
            const distance = Math.abs(sectionCenter - viewportCenter);

            if (distance < minDistance) {
              minDistance = distance;
              activeSection = section.id;
            }
          });
        }

        if (activeSection) {
          setActiveNavLink(activeSection);
        }
      }

      // Throttled scroll handler for better performance
      let scrollTimeout;
      const throttledScroll = () => {
        if (scrollTimeout) return;
        scrollTimeout = setTimeout(() => {
          handleScroll();
          scrollTimeout = null;
        }, 16); // ~60fps
      };

      // Initial call to set correct active state
      setTimeout(handleScroll, 100);

      // Add scroll listener
      window.addEventListener("scroll", throttledScroll, { passive: true });
      window.addEventListener("resize", throttledScroll, { passive: true });

      // Store cleanup function
      window.scrollSpyCleanup = () => {
        window.removeEventListener("scroll", throttledScroll);
        window.removeEventListener("resize", throttledScroll);
        clickHandlers.forEach(({ link, handler }) => {
          link.removeEventListener("click", handler);
        });
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
      };
    };

    // Initialize with a slight delay to ensure DOM is ready
    const initTimeout = setTimeout(initializeScrollSpy, 200);

    // Cleanup
    return () => {
      clearTimeout(initTimeout);
      if (window.scrollSpyCleanup) {
        window.scrollSpyCleanup();
        delete window.scrollSpyCleanup;
      }
    };
  }, [isDarkMode, themeColor]);

  const handleSelectChange = (name, option) => {
    setEditValue(option ? option.value : "");
  };

  const handleTimeZoneSelectChange = (name, option) => {
    setEditValue(option || "");
  };

  const handleToggleChange = (name, checked) => {
    setEditValue(checked);
  };

  const generateSlug = (value = "") =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const normalizeSlug = (value = "") => {
    if (!value || value === "NULL") return "";
    return generateSlug(value);
  };

  const buildCompanyPayload = (data) => {
    const normalizedSlug = normalizeSlug(
      data?.CompanySlug || data?.companySlug || data?.CompName || ""
    );

    return {
      ...data,
      CompanySlug: normalizedSlug,
      companySlug: normalizedSlug,
      CompLatitude: (data?.CompLatitude ?? 0).toString(),
      CompLongitude: (data?.CompLongitude ?? 0).toString(),
    };
  };

  const startLogoEditing = () => {
    setIsLogoEditing(true);
    setLogoPreviousData({
      CompIcon: companyData.CompIcon,
      CompCoverImage: companyData.CompCoverImage,
    });
  };

  const cancelLogoEditing = () => {
    if (logoPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...logoPreviousData }));
      setLogoState({
        CompIcon: logoPreviousData.CompIcon || "",
        logoFile: null,
      });
      setCoverState({
        CompCoverImage: logoPreviousData.CompCoverImage || "",
        coverFile: null,
      });
    }
    setIsLogoEditing(false);
    setLogoPreviousData(null);
  };

  const handleRemoveLogo = () => {
    setLogoState({ CompIcon: "", logoFile: null });
    setCompanyData((prev) => ({ ...prev, CompIcon: "" }));
  };

  const handleRemoveCover = () => {
    setCoverState({ CompCoverImage: "", coverFile: null });
    setCompanyData((prev) => ({ ...prev, CompCoverImage: "" }));
  };

  const startGeneralEditing = () => {
    setIsGeneralEditing(true);
    setGeneralPreviousData({
      CompName: companyData.CompName,
      CompDetail: companyData.CompDetail,
      CompPhone: companyData.CompPhone,
      CompEmail: companyData.CompEmail,
      CompCountry: companyData.CompCountry,
      CompCity: companyData.CompCity,
      CompLanguage: companyData.CompLanguage,
      CompCurrency: companyData.CompCurrency,
      CompTimeZone: companyData.CompTimeZone,
      CompAddress: companyData.CompAddress,
      CompFax: companyData.CompFax,
    });
  };

  const startSocialLinksEditing = () => {
    setIsSocialLinksEditing(true);
    setSocialLinksPreviousData({
      CompWebsite: companyData.CompWebsite,
      CompFacebooklink: companyData.CompFacebooklink,
      CompInstagramLink: companyData.CompInstagramLink,
      CompYoutubeLink: companyData.CompYoutubeLink,
      CompTikTokLink: companyData.CompTikTokLink,
      CompGoogleReview: companyData.CompGoogleReview,
    });
  };

  const startBusinessDetailsEditing = () => {
    setIsBusinessDetailsEditing(true);
    const normalizedLatitude =
      companyData.CompLatitude === "" ||
      companyData.CompLatitude === null ||
      companyData.CompLatitude === undefined
        ? 0
        : companyData.CompLatitude;
    const normalizedLongitude =
      companyData.CompLongitude === "" ||
      companyData.CompLongitude === null ||
      companyData.CompLongitude === undefined
        ? 0
        : companyData.CompLongitude;

    setCompanyData((prev) => ({
      ...prev,
      CompLatitude: normalizedLatitude,
      CompLongitude: normalizedLongitude,
    }));

    setBusinessDetailsPreviousData({
      CompIndustryType: companyData.CompIndustryType,
      CompSecCode: companyData.CompSecCode,
      CompLatitude: normalizedLatitude,
      CompLongitude: normalizedLongitude,
    });
  };

  const startSecurityEditing = () => {
    setIsSecurityEditing(true);
    setSecurityPreviousData({
      CompRecoveryEmail: companyData.CompRecoveryEmail,
      CompRecoveryPhoneNumber: companyData.CompRecoveryPhoneNumber,
    });
  };

  const cancelGeneralEditing = () => {
    if (generalPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...generalPreviousData }));
      setLogoState({
        CompIcon: generalPreviousData.CompIcon || "",
        logoFile: null,
      });
    }
    setIsGeneralEditing(false);
    setGeneralPreviousData(null);
  };

  const cancelSocialLinksEditing = () => {
    if (socialLinksPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...socialLinksPreviousData }));
    }
    setIsSocialLinksEditing(false);
    setSocialLinksPreviousData(null);
  };

  const cancelBusinessDetailsEditing = () => {
    if (businessDetailsPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...businessDetailsPreviousData }));
    }
    setIsBusinessDetailsEditing(false);
    setBusinessDetailsPreviousData(null);
  };

  const cancelSecurityEditing = () => {
    if (securityPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...securityPreviousData }));
    }
    setIsSecurityEditing(false);
    setSecurityPreviousData(null);
  };

  const startEditing = (name, value) => {
    setEditingField(name);
    if (name === "CompCountryCity") {
      setEditValue({
        CompCountry: companyData.CompCountry,
        CompCity: companyData.CompCity,
      });
    } else if (name === "CompIcon") {
      setEditValue(companyData.CompIcon);
      setLogoState((prev) => ({
        ...prev,
        CompIcon: companyData.CompIcon || "",
      }));
    } else {
      setEditValue(value);
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
    setLogoState((prev) => ({
      ...prev,
      CompIcon: companyData.CompIcon || "",
      logoFile: null,
    }));
    setCoverState((prev) => ({
      ...prev,
      CompCoverImage: companyData.CompCoverImage || "",
      coverFile: null,
    }));
  };

  const cancelEditingAfterSubmit = () => {
    setEditingField(null);
    setEditValue("");
    setSelectedCity(null);
    setLogoState({ CompIcon: companyData.CompIcon || "", logoFile: null });
    setCoverState({
      CompCoverImage: companyData.CompCoverImage || "",
      coverFile: null,
    });
  };

  const saveLogoSettings = async () => {
    try {
      setLoading(true);
      const extractUploadedPath = (apiResponse) =>
        apiResponse?.data?.data?.filePath ||
        apiResponse?.data?.data?.imageUrl ||
        apiResponse?.data?.filePath ||
        apiResponse?.data?.imageUrl ||
        apiResponse?.data?.url ||
        "";
      const normalizePath = (pathValue) =>
        pathValue
          ? pathValue.startsWith("/")
            ? pathValue.replace(/\\/g, "/")
            : `/${pathValue.replace(/\\/g, "/")}`
          : "";
      let nextCompanyData = { ...companyData };

      if (!logoState.CompIcon && !logoState.logoFile && logoPreviousData?.CompIcon) {
        await axiosInstance.delete(APIs.DELETE_REMOVE_COMPANY_LOGO);
        setCompanyData((prev) => ({ ...prev, CompIcon: "" }));
        nextCompanyData.CompIcon = "";
      }

      if (!coverState.CompCoverImage && !coverState.coverFile && logoPreviousData?.CompCoverImage) {
        await axiosInstance.delete(APIs.DELETE_REMOVE_COMPANY_COVER_IMAGE);
        setCompanyData((prev) => ({ ...prev, CompCoverImage: "" }));
        nextCompanyData.CompCoverImage = "";
      }

      if (logoState.logoFile) {
        const logoFormData = new FormData();
        logoFormData.append("ImageFile", logoState.logoFile);
        const logoResponse = await axiosInstance.post(APIs.POST_UPDATE_COMPANY_LOGO, logoFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const uploadedLogoPath = extractUploadedPath(logoResponse);
        if (uploadedLogoPath) {
          const logoUrl = normalizePath(uploadedLogoPath);
          const cacheBustedLogoUrl = `${logoUrl}?t=${Date.now()}`;
          setLogoState({ CompIcon: cacheBustedLogoUrl, logoFile: null });
          setCompanyData((prev) => ({ ...prev, CompIcon: cacheBustedLogoUrl }));
          nextCompanyData.CompIcon = logoUrl;
        }
      }

      if (coverState.coverFile) {
        const coverFormData = new FormData();
        coverFormData.append("ImageFile", coverState.coverFile);
        const coverResponse = await axiosInstance.post(APIs.POST_UPDATE_COMPANY_COVER_IMAGE, coverFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const uploadedCoverPath = extractUploadedPath(coverResponse);
        if (uploadedCoverPath) {
          const coverUrl = normalizePath(uploadedCoverPath);
          const cacheBustedCoverUrl = `${coverUrl}?t=${Date.now()}`;
          setCoverState({ CompCoverImage: cacheBustedCoverUrl, coverFile: null });
          setCompanyData((prev) => ({ ...prev, CompCoverImage: cacheBustedCoverUrl }));
          nextCompanyData.CompCoverImage = coverUrl;
        }
      }

      await axiosInstance.put(
        APIs.PUT_UPDATE_COMPANY_DATA,
        buildCompanyPayload(nextCompanyData)
      );

      showSuccessToast("Company images updated successfully");
      setIsLogoEditing(false);
      setLogoPreviousData(null);
      setImageKey(Date.now());
      await fetchCompanyData(false);
    } catch (error) {
      showErrorToast("Failed to update company images");
    } finally {
      setLoading(false);
    }
  };

  const saveGeneralSettings = async () => {
    try {
      setLoading(true);
      const updatedCompanyData = buildCompanyPayload(companyData);
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_COMPANY_DATA,
        updatedCompanyData
      );
      showSuccessToast(
        response.data.Message || "General settings updated successfully"
      );
      setIsGeneralEditing(false);
      setGeneralPreviousData(null);
      // Refresh data without triggering loading state
      await fetchCompanyData(false);
    } catch (error) {
      showErrorToast("Failed to update general settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSocialLinksSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_COMPANY_DATA,
        buildCompanyPayload(companyData)
      );
      showSuccessToast(
        response.data.Message || "Social links updated successfully"
      );
      setIsSocialLinksEditing(false);
      setSocialLinksPreviousData(null);
    } catch (error) {
      showErrorToast("Failed to update Social links");
    } finally {
      setLoading(false);
    }
  };

  const saveBusinessDetailsSettings = async () => {
    try {
      setLoading(true);
      const normalizedCompanyData = {
        ...companyData,
        CompLatitude:
          companyData.CompLatitude === "" ||
          companyData.CompLatitude === null ||
          companyData.CompLatitude === undefined
            ? 0
            : companyData.CompLatitude,
        CompLongitude:
          companyData.CompLongitude === "" ||
          companyData.CompLongitude === null ||
          companyData.CompLongitude === undefined
            ? 0
            : companyData.CompLongitude,
      };
      const updatedCompanyData = buildCompanyPayload(normalizedCompanyData);
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_COMPANY_DATA,
        updatedCompanyData
      );
      showSuccessToast(
        response.data.Message || "Business details updated successfully"
      );
      setIsBusinessDetailsEditing(false);
      setBusinessDetailsPreviousData(null);
      await fetchCompanyData(false);
    } catch (error) {
      showErrorToast("Failed to update business details");
    } finally {
      setLoading(false);
    }
  };

  const saveSecuritySettings = async () => {
    try {
      setLoading(true);
      const updatedCompanyData = buildCompanyPayload(companyData);
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_COMPANY_DATA,
        updatedCompanyData
      );
      showSuccessToast(
        response.data.Message || "Security settings updated successfully"
      );
      setIsSecurityEditing(false);
      setSecurityPreviousData(null);
      await fetchCompanyData(false);
    } catch (error) {
      showErrorToast("Failed to update security settings");
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (name) => {
    try {
      setLoading(true);
      if (name === "CompIcon") {
        if (!logoState.logoFile) {
          showErrorToast("Please select a logo to upload");
          setLoading(false);
          return;
        }
        const logoFormData = new FormData();
        logoFormData.append("ImageFile", logoState.logoFile);
        try {
          const logoResponse = await axiosInstance.post(
            APIs.POST_UPDATE_COMPANY_LOGO,
            logoFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const uploadedLogoUrl =
            logoResponse.data?.data?.filePath ||
            logoResponse.data?.data?.imageUrl ||
            logoResponse.data?.filePath ||
            logoResponse.data?.imageUrl ||
            logoResponse.data?.url;
          if (!uploadedLogoUrl) {
            showErrorToast("Failed to get uploaded logo URL");
            setLoading(false);
            return;
          }
          const cacheBustedUrl = `${uploadedLogoUrl}?t=${Date.now()}`;
          setCompanyData((prev) => ({ ...prev, CompIcon: cacheBustedUrl }));
          setLogoState({ CompIcon: cacheBustedUrl, logoFile: null });
          setImageKey(Date.now());
          showSuccessToast("Company logo updated successfully");
          await fetchCompanyData(false);
          cancelEditingAfterSubmit();
        } catch (err) {
          showErrorToast("Failed to upload logo");
          setLoading(false);
          return;
        }
      } else {
        let updatedCompanyData = { ...companyData };
        if (name === "CompCountryCity") {
          updatedCompanyData = {
            ...updatedCompanyData,
            CompCountry: editValue.CompCountry,
            CompCity: editValue.CompCity,
          };
        } else if (name === "CompTimeZone") {
          updatedCompanyData = {
            ...updatedCompanyData,
            CompTimeZone: editValue.value,
            CompUTCTimeZone: editValue.label,
          };
        } else {
          updatedCompanyData[name] = editValue;
        }
        const alteredData = buildCompanyPayload(updatedCompanyData);
        const response = await axiosInstance.put(
          APIs.PUT_UPDATE_COMPANY_DATA,
          alteredData
        );
        setCompanyData(updatedCompanyData);
        showSuccessToast(
          response.data.Message || `${name} updated successfully`
        );
        cancelEditingAfterSubmit();
      }
    } catch (error) {
      showErrorToast(`Failed to update ${name}`);
    } finally {
      setLoading(false);
    }
  };

  const getFieldIcon = (name) => {
    switch (name) {
      case "CompName":
        return "fa-building";
      case "CompDetail":
        return "fa-info-circle";
      case "CompPhone":
        return "fa-phone";
      case "CompEmail":
        return "fa-envelope";
      case "CompCountryCity":
        return "fa-globe";
      case "CompLanguage":
        return "fa-language";
      case "CompCurrency":
        return "fa-dollar-sign";
      case "CompTimeZone":
        return "fa-clock";
      case "CompAddress":
        return "fa-map-marker-alt";
      case "CompFax":
        return "fa-fax";
      case "CompWebsite":
        return "fa-link";
      case "CompFacebooklink":
        return "fab fa-facebook-f"; // Brand icon
      case "CompInstagramLink":
        return "fab fa-instagram"; // Brand icon
      case "CompYoutubeLink":
        return "fab fa-youtube"; // Brand icon
      case "CompTikTokLink":
        return "fab fa-tiktok"; // Brand icon (requires Font Awesome 5.13.0+)
      case "CompGoogleReview":
        return "fa-star";
      case "CompIndustryType":
        return "fa-industry";
      case "CompSecCode":
        return "fa-key";
      case "CompLatitude":
        return "fa-map-pin";
      case "CompLongitude":
        return "fa-map-pin";
      case "CompPNotificationLimit_PerDay":
        return "fa-bell";
      case "CompMaxIPadLicense":
        return "fa-tablet-alt";
      case "CompMaxUserLicense":
        return "fa-users";
      case "WaiterAppMaxDevices":
        return "fa-mobile-alt";
      case "CompRegDate":
        return "fa-calendar-alt";
      case "CompExpireDate":
        return "fa-calendar-times";
      case "MobileAppExpireDate":
        return "fa-mobile";
      case "WebAppExpireDate":
        return "fa-globe";
      case "WaiterAppExpireDate":
        return "fa-concierge-bell";
      case "IsKitchenApp":
        return "fa-utensils";
      case "ISMobileApp":
        return "fa-mobile-alt";
      case "IsTVApp":
        return "fa-tv";
      case "ISWebApp":
        return "fa-globe";
      case "IsWaiterApp":
        return "fa-concierge-bell";
      case "CompRecoveryEmail":
        return "fa-envelope";
      case "CompRecoveryPhoneNumber":
        return "fa-phone";
      case "CompIcon":
        return "fa-image";
      default:
        return "fa-building";
    }
  };

  const renderField = (
    label,
    name,
    value,
    isEditable = true,
    sectionEditing = false,
    hideIndividualEdit = false
  ) => {
    const isEditing = editingField === name || sectionEditing;

    // Get the current value based on editing mode
    const getCurrentValue = () => {
      if (sectionEditing) {
        // Special handling for CompCountryCity field
        if (name === "CompCountryCity") {
          return {
            CompCountry: companyData.CompCountry,
            CompCity: companyData.CompCity,
          };
        }
        return companyData[name];
      }
      return editValue;
    }

    return (
      <div className="flex-1 p-4" key={name}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <i
                className={`fas ${getFieldIcon(name)} mr-2`}
                style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
              ></i>
              <span
                className={`font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-800"
                }`}
              >
                {label}
              </span>
            </div>
            {isEditing ? (
              <div className="mt-2">
                {name === "UpdateProCPriceAgainstMasterProductCPriceUpdate" ||
                name === "IsKitchenApp" ||
                name === "ISMobileApp" ||
                name === "IsTVApp" ||
                name === "ISWebApp" ||
                name === "IsWaiterApp" ? (
                  <ToggleSwitch
                    isChecked={sectionEditing ? companyData[name] : editValue}
                    onToggle={(checked) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: checked,
                        }));
                      } else {
                        handleToggleChange(name, checked);
                      }
                    }}
                    label={label}
                    name={name}
                  />
                ) : name === "CompCurrency" ? (
                  <Select
                    options={currencies.map((c) => ({
                      value: c.value,
                      label: c.text,
                    }))}
                    value={currencies
                      .filter((c) => c.value === getCurrentValue())
                      .map((c) => ({ value: c.text, label: c.text }))}
                    onChange={(option) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: option ? option.value : "",
                        }));
                      } else {
                        handleSelectChange(name, option);
                      }
                    }}
                    placeholder="Select Currency"
                    isClearable
                    className="text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#374151" : "#ffffff",
                        borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          borderColor: isDarkMode ? "#6b7280" : "#9ca3af",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid #374151"
                          : "1px solid #e5e7eb",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? "#374151"
                            : "#f3f4f6"
                          : isDarkMode
                          ? "#1f2937"
                          : "#ffffff",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                ) : name === "CompTimeZone" ? (
                  <Select
                    options={timeZones.map((c) => ({
                      value: c.value,
                      label: c.text,
                    }))}
                    value={timeZones
                      .filter((c) => c.value === getCurrentValue())
                      .map((c) => ({ value: c.value, label: c.text }))}
                    onChange={(option) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: option ? option.value : "",
                          CompUTCTimeZone: option ? option.label : "",
                        }));
                      } else {
                        handleTimeZoneSelectChange(name, option);
                      }
                    }}
                    placeholder="Select Time Zone"
                    isClearable
                    className="text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#374151" : "#ffffff",
                        borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          borderColor: isDarkMode ? "#6b7280" : "#9ca3af",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid #374151"
                          : "1px solid #e5e7eb",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? "#374151"
                            : "#f3f4f6"
                          : isDarkMode
                          ? "#1f2937"
                          : "#ffffff",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                ) : name === "CompLanguage" ? (
                  <Select
                    options={languages}
                    value={
                      languages.find(
                        (lang) => lang.value === getCurrentValue()
                      ) || null
                    }
                    onChange={(option) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: option ? option.value : "",
                        }));
                      } else {
                        handleSelectChange(name, option);
                      }
                    }}
                    placeholder="Select Language"
                    isClearable
                    className="text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#374151" : "#ffffff",
                        borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          borderColor: isDarkMode ? "#6b7280" : "#9ca3af",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid #374151"
                          : "1px solid #e5e7eb",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? "#374151"
                            : "#f3f4f6"
                          : isDarkMode
                          ? "#1f2937"
                          : "#ffffff",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                ) : name === "CompAddress" ? (
                  <textarea
                    className={`w-full p-3 rounded-lg border transition-colors duration-200 ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-300 bg-gray-50 focus:ring-2 focus:ring-[#232B2B] focus:border-[#232B2B]"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#374151" : "#f9fafb",
                      borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                    rows="4"
                    value={getCurrentValue()}
                    onChange={(e) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: e.target.value,
                        }));
                      } else {
                        setEditValue(e.target.value);
                      }
                    }}
                    placeholder="Enter Company Address"
                  />
                ) : name === "CompIcon" ? (
                  <div className="mt-3">
                    <ImageUpload
                      key={imageKey}
                      imageUrl={logoState.CompIcon || companyData.CompIcon}
                      imageFile={logoState.logoFile}
                      setImage={(url, file) => {
                        setLogoState({ CompIcon: url, logoFile: file });
                        setEditValue(url);
                      }}
                      onRemove={handleRemoveLogo}
                      height="120px"
                      width="150px"
                      marginLeft="0px"
                      buttonText="Change Logo"
                      uploadText="Upload Logo"
                    />
                  </div>
                ) : name === "CompCountryCity" ? (
                  <CountryCitySelector
                    selectedCountry={
                      sectionEditing
                        ? companyData.CompCountry
                        : editValue.CompCountry
                    }
                    selectedCity={
                      sectionEditing ? companyData.CompCity : editValue.CompCity
                    }
                    setSelectedCity={(city) => {
                      setSelectedCity(city);
                      if (sectionEditing) {
                        setCompanyData((prev) => ({ ...prev, CompCity: city }));
                      } else {
                        setEditValue((prev) => ({ ...prev, CompCity: city }));
                      }
                    }}
                    onChange={(updatedData) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          CompCountry: updatedData.CompCountry,
                          CompCity: updatedData.CompCity || "",
                        }));
                      } else {
                        setEditValue((prev) => ({
                          ...prev,
                          CompCountry: updatedData.CompCountry,
                          CompCity: updatedData.CompCity || "",
                        }));
                      }
                    }}
                    setLoading={null}
                    className="flex gap-4"
                    countryClassName="flex-1"
                    cityClassName="flex-1"
                    inlineLayout={true}
                    selectedValueIsLabel={true}
                    countryFieldName="CompCountry"
                    cityFieldName="CompCity"
                    isDarkMode={isDarkMode}
                  />
                ) : name === "CompRegDate" ||
                  name === "CompExpireDate" ||
                  name === "MobileAppExpireDate" ||
                  name === "WebAppExpireDate" ||
                  name === "WaiterAppExpireDate" ? (
                  <FormInput
                    type="date"
                    readOnly={false}
                    value={getCurrentValue()}
                    onchange={(e) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => {
                          const nextValue = e.target.value;

                          if (name === "CompName") {
                            const previousAutoSlug = generateSlug(
                              prev.CompName || ""
                            );
                            const hasCustomSlug =
                              (prev.CompanySlug || "") !== "" &&
                              prev.CompanySlug !== previousAutoSlug;

                            return {
                              ...prev,
                              CompName: nextValue,
                              CompanySlug: hasCustomSlug
                                ? prev.CompanySlug
                                : generateSlug(nextValue),
                            };
                          }

                          if (name === "CompanySlug") {
                            return {
                              ...prev,
                              CompanySlug: normalizeSlug(nextValue),
                            };
                          }

                          return {
                            ...prev,
                            [name]: nextValue,
                          };
                        });
                      } else if (name === "CompanySlug") {
                        setEditValue(normalizeSlug(e.target.value));
                      } else {
                        setEditValue(e.target.value);
                      }
                    }}
                    placeHolder={`Enter ${label}`}
                    style={{
                      borderRadius: "8px",
                      border: isDarkMode
                        ? "1px solid #4b5563"
                        : "1px solid #D9D9D9",
                      background: isDarkMode ? "#374151" : "#F9F9F9",
                      height: "44px",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                ) : (
                  <FormInput
                    type={
                      name.includes("Email")
                        ? "email"
                        : name.includes("Phone") || name.includes("Fax")
                        ? "tel"
                        : name.includes("Latitude") ||
                          name.includes("Longitude") ||
                          name.includes("MinOrderAmount") ||
                          name.includes("CompPNotificationLimit_PerDay") ||
                          name.includes("CompMaxIPadLicense") ||
                          name === "CompMaxUserLicense" ||
                          name.includes("WaiterAppMaxDevices") ||
                          name.includes("LocationLicences")
                        ? "number"
                        : "text"
                    }
                    value={getCurrentValue()}
                    readOnly={false}
                    onchange={(e) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: e.target.value,
                        }));
                      } else {
                        setEditValue(e.target.value);
                      }
                    }}
                    placeHolder={`Enter ${label}`}
                    step={
                      name.includes("Latitude") || name.includes("Longitude")
                        ? "0.000001"
                        : name.includes("CompPNotificationLimit_PerDay")
                        ? "10"
                        : "1"
                    }
                    style={{
                      borderRadius: "8px",
                      border: isDarkMode
                        ? "1px solid #4b5563"
                        : "1px solid #D9D9D9",
                      background: isDarkMode ? "#374151" : "#F9F9F9",
                      height: "44px",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                )}
                {!sectionEditing && (
                  <div className="mt-3 flex gap-3">
                    <button
                      className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                      style={{
                        backgroundColor: themeColor,
                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                        boxShadow: `0 2px 8px ${themeColor}40`,
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                      }}
                      onClick={() => saveField(name)}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                        isDarkMode
                          ? "bg-gray-600 text-white hover:bg-gray-500"
                          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                      }`}
                      style={{
                        backgroundColor: isDarkMode ? "#4b5563" : "#e5e7eb",
                        color: isDarkMode ? "#ffffff" : "#1f2937",
                      }}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {name === "UpdateProCPriceAgainstMasterProductCPriceUpdate" ||
                name === "IsKitchenApp" ||
                name === "ISMobileApp" ||
                name === "IsTVApp" ||
                name === "ISWebApp" ||
                name === "IsWaiterApp" ? (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {value ? "Enabled" : "Disabled"}
                  </span>
                ) : name === "CompRegDate" ||
                  name === "CompExpireDate" ||
                  name === "MobileAppExpireDate" ||
                  name === "WebAppExpireDate" ||
                  name === "WaiterAppExpireDate" ? (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {value ? moment(value).format("DD/MM/YYYY") : "Not set"}
                  </span>
                ) : name === "CompCountryCity" ? (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {`${companyData.CompCountry}${
                      companyData.CompCity ? `, ${companyData.CompCity}` : ""
                    }` || "Not set"}
                  </span>
                ) : name === "CompLanguage" ? (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {languages.find((lang) => lang.value === value)?.label ||
                      "Not set"}
                  </span>
                ) : name === "CompTimeZone" ? (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {timeZones.find((tz) => tz.value === value)?.text ||
                      "Not set"}
                  </span>
                ) : name === "CompUTCTimeZone" ? (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {value || "Not set"}
                  </span>
                ) : name === "CompIcon" ? (
                  value ? (
                    <div className="flex items-center">
                      <img
                        src={`${value}?t=${imageKey}`}
                        alt="Company Logo"
                        className="w-10 h-10 object-contain rounded-lg border border-gray-200"
                        onError={(e) =>
                          (e.target.src = "/path/to/fallback-image.png")
                        }
                      />
                      <span
                        className={`ml-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          isDarkMode
                            ? "bg-green-900 text-green-200"
                            : "bg-green-100 text-green-800"
                        }`}
                        style={{
                          backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                          color: isDarkMode ? "#bbf7d0" : "#166534",
                        }}
                      >
                        Logo uploaded
                      </span>
                    </div>
                  ) : (
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                        isDarkMode
                          ? "bg-green-900 text-green-200"
                          : "bg-green-100 text-green-800"
                      }`}
                      style={{
                        backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                        color: isDarkMode ? "#bbf7d0" : "#166534",
                      }}
                    >
                      No logo uploaded
                    </span>
                  )
                ) : (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      isDarkMode
                        ? "bg-green-900 text-green-200"
                        : "bg-green-100 text-green-800"
                    }`}
                    style={{
                      backgroundColor: isDarkMode ? "#14532d" : "#dcfce7",
                      color: isDarkMode ? "#bbf7d0" : "#166534",
                    }}
                  >
                    {value || "Not set"}
                  </span>
                )}
              </div>
            )}
          </div>
          {isEditable &&
            !isEditing &&
            !sectionEditing &&
            !hideIndividualEdit && (
              <button
                className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                  isDarkMode
                    ? "bg-gray-600 text-white hover:bg-gray-500"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
                style={{
                  backgroundColor: isDarkMode ? "#4b5563" : "#f3f4f6",
                  color: isDarkMode ? "#ffffff" : "#1f2937",
                }}
                onClick={() => startEditing(name, value)}
              >
                Edit
              </button>
            )}
        </div>
      </div>
    );
  };

  const renderFieldRow = (fields) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((field) =>
          renderField(
            field.label,
            field.name,
            field.value,
            field.isEditable,
            field.sectionEditing,
            field.hideIndividualEdit
          )
        )}
      </div>
    );
  };

  // Show loading state while data is being fetched
  if (loading || !companyData.CompID) {
    return (
      <div
        className={`min-h-screen font-sans ${
          isDarkMode ? "bg-gray-900" : "bg-gray-200"
        }`}
        style={{
          backgroundColor: isDarkMode ? "#1a1a1a" : "#f3f4f6",
          color: isDarkMode ? "#ffffff" : "#000000",
        }}
      >
        <link
          href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css"
          rel="stylesheet"
        />
        <link
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"
          rel="stylesheet"
        />
        <Preloader />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-sans ${
        isDarkMode ? "bg-gray-900" : "bg-gray-200"
      }`}
      style={{
        backgroundColor: isDarkMode ? "#1a1a1a" : "#f3f4f6",
        color: isDarkMode ? "#ffffff" : "#000000",
      }}
    >
      <link
        href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css"
        rel="stylesheet"
      />
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"
        rel="stylesheet"
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li className="inline-flex items-center">
                <a
                  href="#/"
                  className={`text-sm font-medium ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-[#232B2B]"
                  }`}
                  style={{
                    color: isDarkMode ? "#d1d5db" : "#374151",
                    "--hover-color": themeColor,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = themeColor;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = isDarkMode ? "#d1d5db" : "#374151";
                  }}
                >
                  Home
                </a>
              </li>
              <li>
                <div className="flex items-center">
                  <svg
                    className="text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    style={{
                      width: "16px",
                      height: "16px",
                      flexShrink: 0,
                    }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span
                    className={`ml-1 text-sm font-medium ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Profile
                  </span>
                </div>
              </li>
            </ol>
          </nav>
          <h1
            className={`text-3xl font-bold mt-2 ${
              isDarkMode ? "text-white" : "text-gray-800"
            }`}
          >
            Company Profile
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <aside
            className={`lg:w-1/4 rounded-lg p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
            style={{ backgroundColor: isDarkMode ? "#1f2937" : "#ffffff" }}
          >
            <nav
              className={`sticky top-4 rounded-lg p-6 ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
              style={{ backgroundColor: isDarkMode ? "#1f2937" : "#ffffff" }}
            >
              <div className="space-y-1" id="Target">
                {[
                  {
                    id: "companyLogo",
                    label: "Brand Images",
                    icon: "fa-image",
                  },
                  { id: "general", label: "General", icon: "fa-user" },
                  { id: "socialLinks", label: "Social Links", icon: "fa-link" },
                  {
                    id: "businessDetails",
                    label: "Business Details",
                    icon: "fa-cog",
                  },
                  { id: "security", label: "Security", icon: "fa-shield-alt" },
                  { id: "licenses", label: "Licenses", icon: "fa-file-alt" },
                ].map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 relative ${
                      isDarkMode
                        ? "text-gray-300 hover:text-white hover:bg-gray-700"
                        : "text-gray-700 hover:text-[#232B2B] hover:bg-gray-100"
                    }`}
                    data-toggle="scroll-to"
                    style={{
                      borderLeft: "3px solid transparent",
                      marginLeft: "-3px",
                      borderRadius: "6px",
                    }}
                  >
                    <i className={`fas ${item.icon} mr-3 text-sm`}></i>
                    <span className="font-semibold">{item.label}</span>
                  </a>
                ))}
              </div>
            </nav>
          </aside>

          <main className="lg:w-3/4 space-y-8" id="bsSpyContent">
            {companyData.CompID ? (
              <>
                <section
                  id="companyLogo"
                  className={`rounded-lg p-6 transform transition-all duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  }}
                >
                  <div className="flex items-center mb-4">
                    <Icon
                      icon="solar:image-bold-duotone"
                      className={`text-2xl mr-3 ${
                        isDarkMode ? "text-white" : "text-[#232B2B]"
                      }`}
                      style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
                    />
                    <div>
                      <h2
                        className={`text-xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Brand Images
                      </h2>
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Upload and manage your logo and cover image
                      </p>
                    </div>
                  </div>
                  <div
                    className="border-t pt-4"
                    style={{ borderColor: isDarkMode ? "#374151" : "#e5e7eb" }}
                  >
                    {!isLogoEditing ? (
                      <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="rounded-lg border p-4">
                            <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                              Current Logo (Small)
                            </h3>
                            <div className="w-24 h-24 rounded-lg border overflow-hidden flex items-center justify-center">
                              {companyData.CompIcon ? (
                                <img
                                  src={`${companyData.CompIcon}?t=${imageKey}`}
                                  alt="Company Logo"
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <i className="fas fa-image text-gray-400"></i>
                              )}
                            </div>
                          </div>
                          <div className="rounded-lg border p-4">
                            <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                              Current Cover Image (Large)
                            </h3>
                            <div className="w-full h-24 rounded-lg border overflow-hidden flex items-center justify-center">
                              {companyData.CompCoverImage ? (
                                <img
                                  src={`${companyData.CompCoverImage}?t=${imageKey}`}
                                  alt="Company Cover"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <i className="fas fa-image text-gray-400"></i>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={startLogoEditing}
                          >
                            <i className="fas fa-edit mr-2"></i>
                            Edit Images
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3
                          className={`text-sm font-medium mb-3 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <i
                            className="fas fa-upload mr-2"
                            style={{
                              color: isDarkMode ? "#ffffff" : "#232B2B",
                            }}
                          ></i>
                          Upload New Logo
                        </h3>
                        <div className="grid grid-cols-1 gap-4 mb-4">
                          <div
                            className={`rounded-lg border p-4 ${
                              isDarkMode
                                ? "border-gray-600 bg-gray-700"
                                : "border-gray-200 bg-white"
                            }`}
                          >
                            <p
                              className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Logo (Small)
                            </p>
                            <ImageUpload
                              key={imageKey}
                              imageUrl={logoState.CompIcon || companyData.CompIcon}
                              imageFile={logoState.logoFile}
                              setImage={(url, file) =>
                                setLogoState({ CompIcon: url, logoFile: file })
                              }
                              onRemove={handleRemoveLogo}
                              height="150px"
                              width="220px"
                              marginLeft="0px"
                              buttonText="Choose Logo"
                              uploadText="Upload Logo"
                            />
                            <span
                              className={`text-xs mt-2 block ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {logoState.logoFile
                                ? logoState.logoFile.name
                                : companyData.CompIcon
                                ? "Saved logo"
                                : "No logo selected"}
                            </span>
                          </div>

                          <div
                            className={`rounded-lg border p-4 ${
                              isDarkMode
                                ? "border-gray-600 bg-gray-700"
                                : "border-gray-200 bg-white"
                            }`}
                          >
                            <p
                              className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Cover Image (Large)
                            </p>
                            <ImageUpload
                              key={`${imageKey}-cover`}
                              imageUrl={
                                coverState.CompCoverImage || companyData.CompCoverImage
                              }
                              imageFile={coverState.coverFile}
                              setImage={(url, file) =>
                                setCoverState({ CompCoverImage: url, coverFile: file })
                              }
                              onRemove={handleRemoveCover}
                              height="200px"
                              width="100%"
                              marginLeft="0px"
                              buttonText="Choose Cover"
                              uploadText="Upload Cover"
                              useGrid={false}
                            />
                            <span
                              className={`text-xs mt-2 block ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {coverState.coverFile
                                ? coverState.coverFile.name
                                : companyData.CompCoverImage
                                ? "Saved cover image"
                                : "No cover selected"}
                            </span>
                          </div>
                        </div>
                        <p
                          className={`text-xs mt-3 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <i
                            className="fas fa-info-circle mr-1"
                            style={{
                              color: isDarkMode ? "#9ca3af" : "#6b7280",
                            }}
                          ></i>
                          Logo recommended: 300x300px. Cover recommended:
                          1200x400px. Supported formats: JPG, PNG, GIF
                        </p>
                        <div className="mt-4 flex justify-end gap-3">
                          <button
                            className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                              isDarkMode
                                ? "bg-gray-600 text-white hover:bg-gray-500"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                            }`}
                            style={{
                              backgroundColor: isDarkMode
                                ? "#4b5563"
                                : "#e5e7eb",
                              color: isDarkMode ? "#ffffff" : "#1f2937",
                            }}
                            onClick={cancelLogoEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times mr-2"></i>
                            Cancel
                          </button>
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={saveLogoSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full mr-2"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save mr-2"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section
                  id="general"
                  className={`rounded-lg p-6 transform transition-all duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  }}
                >
                  <div className="flex items-center mb-4">
                    <Icon
                      icon="solar:user-bold-duotone"
                      className={`text-2xl mr-3 ${
                        isDarkMode ? "text-white" : "text-[#232B2B]"
                      }`}
                      style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
                    />
                    <div>
                      <h2
                        className={`text-xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        General
                      </h2>
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        View and update your company general information.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {!isGeneralEditing ? (
                      <>
                        {renderFieldRow([
                          {
                            label: "Company Name",
                            name: "CompName",
                            value: companyData.CompName,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Company Slug",
                            name: "CompanySlug",
                            value: companyData.CompanySlug,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Company Description",
                            name: "CompDetail",
                            value: companyData.CompDetail,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Contact Number",
                            name: "CompPhone",
                            value: companyData.CompPhone,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Email Address",
                            name: "CompEmail",
                            value: companyData.CompEmail,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Country & City",
                            name: "CompCountryCity",
                            value: {
                              CompCountry: companyData.CompCountry,
                              CompCity: companyData.CompCity,
                            },
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Language",
                            name: "CompLanguage",
                            value: companyData.CompLanguage,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Currency",
                            name: "CompCurrency",
                            value: companyData.CompCurrency,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Time Zone",
                            name: "CompTimeZone",
                            value: companyData.CompTimeZone,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Address",
                            name: "CompAddress",
                            value: companyData.CompAddress,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Fax",
                            name: "CompFax",
                            value: companyData.CompFax,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end">
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={startGeneralEditing}
                          >
                            Edit
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {renderFieldRow([
                          {
                            label: "Company Name",
                            name: "CompName",
                            value: companyData.CompName,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Company Slug",
                            name: "CompanySlug",
                            value: companyData.CompanySlug,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Company Description",
                            name: "CompDetail",
                            value: companyData.CompDetail,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Contact Number",
                            name: "CompPhone",
                            value: companyData.CompPhone,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Email Address",
                            name: "CompEmail",
                            value: companyData.CompEmail,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Country & City",
                            name: "CompCountryCity",
                            value: {
                              CompCountry: companyData.CompCountry,
                              CompCity: companyData.CompCity,
                            },
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Language",
                            name: "CompLanguage",
                            value: companyData.CompLanguage,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Currency",
                            name: "CompCurrency",
                            value: companyData.CompCurrency,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Time Zone",
                            name: "CompTimeZone",
                            value: companyData.CompTimeZone,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Address",
                            name: "CompAddress",
                            value: companyData.CompAddress,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Fax",
                            name: "CompFax",
                            value: companyData.CompFax,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end gap-3">
                          <button
                            className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                              isDarkMode
                                ? "bg-gray-600 text-white hover:bg-gray-500"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                            }`}
                            style={{
                              backgroundColor: isDarkMode
                                ? "#4b5563"
                                : "#e5e7eb",
                              color: isDarkMode ? "#ffffff" : "#1f2937",
                            }}
                            onClick={cancelGeneralEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times mr-2"></i>
                            Cancel
                          </button>
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={saveGeneralSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full mr-2"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save mr-2"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section
                  id="socialLinks"
                  className={`rounded-lg p-6 transform transition-all duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  }}
                >
                  <div className="flex items-center mb-4">
                    <Icon
                      icon="solar:link-bold-duotone"
                      className={`text-2xl mr-3 ${
                        isDarkMode ? "text-white" : "text-[#232B2B]"
                      }`}
                      style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
                    />
                    <div>
                      <h2
                        className={`text-xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Social Links
                      </h2>
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Manage your company's social media and website links.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {!isSocialLinksEditing ? (
                      <>
                        {renderFieldRow([
                          {
                            label: "Website",
                            name: "CompWebsite",
                            value: companyData.CompWebsite,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Facebook Link",
                            name: "CompFacebooklink",
                            value: companyData.CompFacebooklink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Instagram Link",
                            name: "CompInstagramLink",
                            value: companyData.CompInstagramLink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "YouTube Link",
                            name: "CompYoutubeLink",
                            value: companyData.CompYoutubeLink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "TikTok Link",
                            name: "CompTikTokLink",
                            value: companyData.CompTikTokLink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Google Review Link",
                            name: "CompGoogleReview",
                            value: companyData.CompGoogleReview,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end">
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={startSocialLinksEditing}
                          >
                            Edit
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {renderFieldRow([
                          {
                            label: "Website",
                            name: "CompWebsite",
                            value: companyData.CompWebsite,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Facebook Link",
                            name: "CompFacebooklink",
                            value: companyData.CompFacebooklink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Instagram Link",
                            name: "CompInstagramLink",
                            value: companyData.CompInstagramLink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "YouTube Link",
                            name: "CompYoutubeLink",
                            value: companyData.CompYoutubeLink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "TikTok Link",
                            name: "CompTikTokLink",
                            value: companyData.CompTikTokLink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Google Review Link",
                            name: "CompGoogleReview",
                            value: companyData.CompGoogleReview,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end gap-3">
                          <button
                            className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                              isDarkMode
                                ? "bg-gray-600 text-white hover:bg-gray-500"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                            }`}
                            style={{
                              backgroundColor: isDarkMode
                                ? "#4b5563"
                                : "#e5e7eb",
                              color: isDarkMode ? "#ffffff" : "#1f2937",
                            }}
                            onClick={cancelSocialLinksEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times mr-2"></i>
                            Cancel
                          </button>
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={saveSocialLinksSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full mr-2"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save mr-2"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section
                  id="businessDetails"
                  className={`rounded-lg p-6 transform transition-all duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  }}
                >
                  <div className="flex items-center mb-4">
                    <Icon
                      icon="solar:cog-bold-duotone"
                      className={`text-2xl mr-3 ${
                        isDarkMode ? "text-white" : "text-[#232B2B]"
                      }`}
                      style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
                    />
                    <div>
                      <h2
                        className={`text-xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Business Details
                      </h2>
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Manage your company business settings.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {!isBusinessDetailsEditing ? (
                      <>
                        {renderFieldRow([
                          {
                            label: "Business Type",
                            name: "CompIndustryType",
                            value: companyData.CompIndustryType,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Binding Code",
                            name: "CompSecCode",
                            value: companyData.CompSecCode,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Latitude",
                            name: "CompLatitude",
                            value: companyData.CompLatitude,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Longitude",
                            name: "CompLongitude",
                            value: companyData.CompLongitude,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end">
                          <button
                            className="px-4 py-2 text-white rounded-lg hover:bg-[#1C2525] transition-colors duration-200"
                            style={{ backgroundColor: "#232B2B" }}
                            onClick={startBusinessDetailsEditing}
                          >
                            Edit
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {renderFieldRow([
                          {
                            label: "Business Type",
                            name: "CompIndustryType",
                            value: companyData.CompIndustryType,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Binding Code",
                            name: "CompSecCode",
                            value: companyData.CompSecCode,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        {renderFieldRow([
                          {
                            label: "Latitude",
                            name: "CompLatitude",
                            value: companyData.CompLatitude,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Longitude",
                            name: "CompLongitude",
                            value: companyData.CompLongitude,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        <div className="px-6 pb-2">
                          <CoordinatePicker
                            latitude={companyData.CompLatitude}
                            longitude={companyData.CompLongitude}
                            addressHint={[
                              companyData.CompAddress,
                              companyData.CompCity,
                              companyData.CompCountry,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                            onSelect={(lat, lng) =>
                              setCompanyData((prev) => ({
                                ...prev,
                                CompLatitude: lat,
                                CompLongitude: lng,
                              }))
                            }
                          />
                        </div>
                        <div className="py-4 px-6 flex justify-end gap-3">
                          <button
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                            onClick={cancelBusinessDetailsEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times mr-2"></i>
                            Cancel
                          </button>
                          <button
                            className="px-4 py-2 text-white rounded-lg hover:bg-[#1C2525] transition-colors duration-200"
                            style={{ backgroundColor: "#232B2B" }}
                            onClick={saveBusinessDetailsSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full mr-2"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save mr-2"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section
                  id="security"
                  className={`rounded-lg p-6 transform transition-all duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  }}
                >
                  <div className="flex items-center mb-4">
                    <Icon
                      icon="solar:shield-bold-duotone"
                      className={`text-2xl mr-3 ${
                        isDarkMode ? "text-white" : "text-[#232B2B]"
                      }`}
                      style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
                    />
                    <div>
                      <h2
                        className={`text-xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Security
                      </h2>
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Altepos may reach out regarding suspicious account
                        activity, assist with account access and recovery, and
                        send transactional messages related to your account.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {!isSecurityEditing ? (
                      <>
                        {renderFieldRow([
                          {
                            label: "Security Contact Email",
                            name: "CompRecoveryEmail",
                            value: companyData.CompRecoveryEmail,
                            isEditable: true,
                            sectionEditing: isSecurityEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Security Contact Phone",
                            name: "CompRecoveryPhoneNumber",
                            value: companyData.CompRecoveryPhoneNumber,
                            isEditable: true,
                            sectionEditing: isSecurityEditing,
                            hideIndividualEdit: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end">
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={startSecurityEditing}
                          >
                            Edit
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {renderFieldRow([
                          {
                            label: "Security Contact Email",
                            name: "CompRecoveryEmail",
                            value: companyData.CompRecoveryEmail,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Security Contact Phone",
                            name: "CompRecoveryPhoneNumber",
                            value: companyData.CompRecoveryPhoneNumber,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ])}
                        <div className="py-4 px-6 flex justify-end gap-3">
                          <button
                            className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                              isDarkMode
                                ? "bg-gray-600 text-white hover:bg-gray-500"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                            }`}
                            style={{
                              backgroundColor: isDarkMode
                                ? "#4b5563"
                                : "#e5e7eb",
                              color: isDarkMode ? "#ffffff" : "#1f2937",
                            }}
                            onClick={cancelSecurityEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times mr-2"></i>
                            Cancel
                          </button>
                          <button
                            className="px-4 py-2 text-white rounded-lg transition-colors duration-200"
                            style={{
                              backgroundColor: themeColor,
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              boxShadow: `0 2px 8px ${themeColor}40`,
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                            }}
                            onClick={saveSecuritySettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full mr-2"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save mr-2"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                <section
                  id="licenses"
                  className={`rounded-lg p-6 transform transition-all duration-300 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  }}
                >
                  <div className="flex items-center mb-4">
                    <Icon
                      icon="solar:document-bold-duotone"
                      className={`text-2xl mr-3 ${
                        isDarkMode ? "text-white" : "text-[#232B2B]"
                      }`}
                      style={{ color: isDarkMode ? "#ffffff" : "#232B2B" }}
                    />
                    <div>
                      <h2
                        className={`text-xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Licenses
                      </h2>
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        View your company licenses.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {renderFieldRow([
                      {
                        label: "Max ePOS Devices License",
                        name: "CompMaxIPadLicense",
                        value: companyData.CompMaxIPadLicense,
                        isEditable: false,
                      },
                      {
                        label: "Max User License",
                        name: "CompMaxUserLicense",
                        value: companyData.CompMaxUserLicense,
                        isEditable: false,
                      },
                    ])}
                    {renderFieldRow([
                      {
                        label: "Registration Start Date",
                        name: "CompRegDate",
                        value: companyData.CompRegDate,
                        isEditable: false,
                      },
                      {
                        label: "Registration End Date",
                        name: "CompExpireDate",
                        value: companyData.CompExpireDate,
                        isEditable: false,
                      },
                    ])}
                    {renderFieldRow([
                      {
                        label: "Mobile App Expiry Date",
                        name: "MobileAppExpireDate",
                        value: companyData.MobileAppExpireDate,
                        isEditable: false,
                      },
                      {
                        label: "Mobile App Enabled",
                        name: "ISMobileApp",
                        value: companyData.ISMobileApp,
                        isEditable: false,
                      },
                    ])}
                  </div>
                </section>
              </>
            ) : (
              <div
                className={`border-l-4 p-4 rounded-lg ${
                  isDarkMode
                    ? "bg-yellow-900 border-yellow-600"
                    : "bg-yellow-50 border-yellow-400"
                }`}
                style={{
                  backgroundColor: isDarkMode ? "#451a03" : "#fefce8",
                  borderColor: isDarkMode ? "#d97706" : "#facc15",
                }}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <i
                      className={`fas fa-exclamation-triangle ${
                        isDarkMode ? "text-yellow-400" : "text-yellow-400"
                      }`}
                      style={{ color: isDarkMode ? "#fbbf24" : "#facc15" }}
                    ></i>
                  </div>
                  <div className="ml-3">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-yellow-200" : "text-yellow-700"
                      }`}
                      style={{ color: isDarkMode ? "#fde68a" : "#a16207" }}
                    >
                      No company data available. Please try refreshing the page
                      or contact support.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default Profile;